import type { CollectionConfig, PayloadRequest } from 'payload'

import { httpFetch } from '@/server/http/index.js'
import { maybeCreateRedirect, maybeGenerateSignedUrl } from '@/server/payload/tokenAuth.js'
import { buildStorageCdnUrl } from '@/server/urls.js'
import { copyHeaders, createProxyResponse } from '@/shared/http.js'
import type { NormalizedSignedUrlsConfig, NormalizedStorageConfig } from '@/shared/types/index.js'

type Args = {
  collection: CollectionConfig
  filename: string
  prefix?: string
  req: PayloadRequest
  signedUrls: false | NormalizedSignedUrlsConfig
  storageConfig: NormalizedStorageConfig
  usePayloadAccessControl: boolean
}

export const storageStaticHandler = async ({
  collection,
  filename,
  prefix,
  req,
  signedUrls,
  storageConfig,
  usePayloadAccessControl,
}: Args): Promise<Response> => {
  let baseUrl = buildStorageCdnUrl(storageConfig.hostname, prefix || '', filename)

  if (req.url) {
    const requestUrl = new URL(req.url, `http://${req.headers.get('host') || 'localhost'}`)
    requestUrl.searchParams.delete('prefix')
    const forwardedSearch = requestUrl.searchParams.toString()
    if (forwardedSearch) {
      baseUrl += `?${forwardedSearch}`
    }
  }

  const context = {
    collection,
    filename,
    signedUrls,
    tokenSecurityKey: storageConfig.tokenSecurityKey,
    usePayloadAccessControl,
  }

  const redirect = maybeCreateRedirect(baseUrl, { ...context, req })
  if (redirect) {
    return redirect
  }

  const rangeHeader = req.headers.get('range')
  const requestHeaders = new Headers()
  if (rangeHeader) {
    requestHeaders.set('Range', rangeHeader)
  }

  const fetchUrl = maybeGenerateSignedUrl(baseUrl, context)

  const response = await httpFetch(fetchUrl, {
    headers: requestHeaders,
    stream: true,
    throwHttpErrors: false,
  })

  if (!response.ok && response.status !== 206) {
    return new Response(null, { status: 404, statusText: 'Not Found' })
  }

  const etagFromHeaders = req.headers.get('etag') || req.headers.get('if-none-match')
  const objectEtag = response.headers.get('etag')

  if (etagFromHeaders && objectEtag && etagFromHeaders === objectEtag) {
    return new Response(null, {
      headers: copyHeaders(response.headers),
      status: 304,
    })
  }

  return createProxyResponse(response)
}
